import { tx, txt, useContent } from "@/content/ContentContext";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, LogOut, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { PlanCards } from "@/components/PlanCards";
import { BrandMark } from "@/components/AppShell";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useContact } from "@/context/ContactContext";
import { CONTACT_EMAIL, planCurrency } from "@/lib/plans";
import { AddonCard } from "@/components/AddonCard";

export function UpgradePanel({ locked }) {
  useContent();
  const contact = useContact();
  const { session } = useAuth();
  const biz = session.business;
  const isOwner = session.user.role === "owner";
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState("");
  const [requested, setRequested] = useState(null);

  useEffect(() => {
    api.get("/plans/mine").then((r) => setPlans(r.data)).catch((e) => toast.error(errMsg(e)));
    if (isOwner) api.get("/upgrade-requests/mine").then((r) => setRequested(r.data.find((x) => x.status === "pending")?.plan_code || null)).catch(() => {});
  }, [isOwner]);

  const request = async (p) => {
    setBusy(p.code);
    try {
      const { data } = await api.post("/upgrade-requests", { plan_code: p.code, currency: planCurrency(biz.currency) });
      setNotice(data.message);
      data.created ? toast.success(data.message) : toast.info(data.message);
      if (data.created) setRequested(p.code);
      else api.get("/upgrade-requests/mine").then((r) => setRequested(r.data.find((x) => x.status === "pending")?.plan_code || null));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-8" data-testid={locked ? "demo-lock-screen" : "upgrade-page"}>
      <div className="max-w-2xl">
        {locked && (
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-rose-100 px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-widest text-rose-700">
            <Lock className="h-3.5 w-3.5" /> {txt("upgrade.account_locked")}
          </div>
        )}
        <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl" data-testid="upgrade-title">
          {locked ? "Your free demo has ended" : tx("upgrade.pick_a_plan_for") + biz.name}
        </h1>
        <p className="mt-3 text-base text-slate-600">
          {locked
            ? isOwner
              ? tx("upgrade.your_products_and_sales_are")
              : tx("upgrade.your_products_and_sales_are_2", { name: biz.name })
            : tx("upgrade.days_left", { days: biz.demo.days_left })}
        </p>
        {notice && (
          <div className="mt-5 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800" data-testid="upgrade-notice">
            <CheckCircle2 className="h-4 w-4" /> {notice}
          </div>
        )}
      </div>
      <div className="mt-10">
        <PlanCards plans={plans} currency={planCurrency(biz.currency)} mode={isOwner ? "upgrade" : "view"} onAction={request} busyCode={busy} requestedCode={requested} />
        <AddonCard currency={planCurrency(biz.currency)} />
        <AddonCard code="reports" currency={planCurrency(biz.currency)} />
      </div>
      <div className="mt-8 flex items-center gap-2 text-sm text-slate-500">
        <Mail className="h-4 w-4 text-amber-600" /> {txt("upgrade.prefer_to_talk")} <a className="font-semibold text-slate-900 underline decoration-amber-500 underline-offset-4" href={`mailto:${CONTACT_EMAIL}`} data-testid="upgrade-contact-email">{CONTACT_EMAIL}</a>
        <span className="text-slate-300">·</span>
        <button onClick={() => contact("support")} className="font-semibold text-slate-900 underline decoration-amber-500 underline-offset-4" data-testid="upgrade-contact-link">{txt("upgrade.send_us_a_message")}</button>
      </div>
    </div>
  );
}

export function LockScreen() {
  useContent();
  const { signOut } = useAuth();
  const nav = useNavigate();
  return (
    <div className="min-h-[100dvh] bg-slate-50">
      <header className="grain flex h-14 items-center justify-between bg-slate-950 px-4 sm:px-6">
        <BrandMark />
        <button onClick={() => { signOut(); nav("/login"); }} data-testid="lock-logout-button" className="flex h-10 items-center gap-2 rounded-lg px-3 text-sm font-semibold text-slate-300 hover:bg-white/10">
          <LogOut className="h-4 w-4" /> {txt("upgrade.log_out")}
        </button>
      </header>
      <UpgradePanel locked />
    </div>
  );
}

export default function Upgrade() {
  useContent();
  return (
    <div className="h-full overflow-y-auto">
      <UpgradePanel locked={false} />
    </div>
  );
}
